
import { useState } from 'react';

interface ContactSectionProps {
  language: 'es' | 'en';
}

export default function ContactSection({ language }: ContactSectionProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    pack: '',
    arrivalDate: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const content = {
    es: {
      title: 'Agendá tu asesoría',
      subtitle: 'Comienza tu nueva vida en Japón con el pie derecho',
      form: {
        name: 'Nombre completo',
        email: 'Email',
        phone: 'WhatsApp/Teléfono',
        pack: 'Pack de interés',
        arrivalDate: 'Fecha de llegada a Japón',
        message: 'Cuéntanos sobre tu situación',
        submit: 'Enviar consulta',
        submitting: 'Enviando...'
      },
      packs: [
        'Municipal Pack - USD 250',
        'Life Start Pack - USD 425',
        'Premium Step Pack - USD 600',
        'Consulta personalizada'
      ],
      contact: {
        title: 'Información de contacto',
        whatsapp: 'WhatsApp',
        email: 'Email',
        location: 'Ubicación',
        hours: 'Horarios de atención'
      },
      success: '¡Mensaje enviado! Te contactaremos pronto.',
      error: 'Error al enviar. Intenta nuevamente.'
    },
    en: {
      title: 'Book your consultation',
      subtitle: 'Start your new life in Japan on the right foot',
      form: {
        name: 'Full name',
        email: 'Email',
        phone: 'WhatsApp/Phone',
        pack: 'Pack of interest',
        arrivalDate: 'Arrival date in Japan',
        message: 'Tell us about your situation',
        submit: 'Send inquiry',
        submitting: 'Sending...'
      },
      packs: [
        'Municipal Pack - USD 250',
        'Life Start Pack - USD 425',
        'Premium Step Pack - USD 600',
        'Custom consultation'
      ],
      contact: {
        title: 'Contact information',
        whatsapp: 'WhatsApp',
        email: 'Email',
        location: 'Location',
        hours: 'Business hours'
      },
      success: 'Message sent! We will contact you soon.',
      error: 'Error sending. Please try again.'
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3p9a0jtpgsu3r1tncvg', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          pack: '',
          arrivalDate: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-red-50 to-yellow-50">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-red-600 mb-4">
              {content[language].title} 📞
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {content[language].subtitle}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <form id="consultation-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    {content[language].form.name} *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    {content[language].form.email} *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    {content[language].form.phone}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    {content[language].form.pack} *
                  </label>
                  <div className="relative">
                    <select
                      name="pack"
                      value={formData.pack}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors appearance-none bg-white"
                    >
                      <option value="">{language === 'es' ? 'Selecciona un pack' : 'Select a pack'}</option>
                      {content[language].packs.map((pack, index) => (
                        <option key={index} value={pack}>{pack}</option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    {content[language].form.arrivalDate}
                  </label>
                  <input
                    type="date"
                    name="arrivalDate"
                    value={formData.arrivalDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    {content[language].form.message}
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors resize-none"
                    placeholder={language === 'es' ? 'Cuéntanos sobre tu situación, dudas o necesidades específicas...' : 'Tell us about your situation, doubts or specific needs...'}
                  />
                  <div className="text-right text-sm text-gray-500 mt-1">
                    {formData.message.length}/500
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-bold py-4 px-6 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? content[language].form.submitting : content[language].form.submit}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
                    {content[language].success}
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
                    {content[language].error}
                  </div>
                )}
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h3 className="text-2xl font-bold text-red-600 mb-6">
                  {content[language].contact.title}
                </h3>

                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                      <i className="ri-whatsapp-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">{content[language].contact.whatsapp}</h4>
                      <p className="text-gray-600">+54 11 4025-6200</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                      <i className="ri-mail-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">{content[language].contact.email}</h4>
                      <p className="text-gray-600">firstepjp@gmail.com</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center">
                      <i className="ri-map-pin-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">{content[language].contact.location}</h4>
                      <p className="text-gray-600">Osaka, Japan</p>
                      <p className="text-gray-500 text-sm">{language === 'es' ? 'También atendemos Tokio' : 'We also serve Tokyo'}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                      <i className="ri-time-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">{content[language].contact.hours}</h4>
                      <p className="text-gray-600">
                        {language === 'es' ? 'Lun - Vie: 9:00 - 18:00 JST' : 'Mon - Fri: 9:00 - 18:00 JST'}
                      </p>
                      <p className="text-gray-500 text-sm">
                        {language === 'es' ? 'Premium: 24/7 WhatsApp' : 'Premium: 24/7 WhatsApp'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Methods */}
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h3 className="text-xl font-bold text-red-600 mb-4">
                  {language === 'es' ? 'Métodos de pago' : 'Payment methods'}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg">
                    <i className="ri-paypal-line text-blue-600 text-xl"></i>
                    <span className="text-sm font-medium">PayPal</span>
                  </div>
                  <div className="flex items-center space-x-2 p-3 bg-green-50 rounded-lg">
                    <i className="ri-bank-line text-green-600 text-xl"></i>
                    <span className="text-sm font-medium">Wise</span>
                  </div>
                  <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                    <i className="ri-bank-card-line text-gray-600 text-xl"></i>
                    <span className="text-sm font-medium">{language === 'es' ? 'Transferencia' : 'Bank Transfer'}</span>
                  </div>
                  <div className="flex items-center space-x-2 p-3 bg-yellow-50 rounded-lg">
                    <i className="ri-money-yen-circle-line text-yellow-600 text-xl"></i>
                    <span className="text-sm font-medium">{language === 'es' ? 'Efectivo' : 'Cash'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
